import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/forms/x-input.js';
import '@adornis/popover/x-dropdown-selection.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { DateTime } from 'luxon';
import { filter, from, map, switchMap } from 'rxjs';
import { ExamDay } from '../../db/examdays.js';
import type { ILSUser } from '../../db/ils-user.js';
import { type Order, createNewOrderManually } from '../../db/order.js';
import './ils-examday-select-item.js';

/**
 * @element ils-admin-create-order
 */
@customElement('ils-admin-create-order')
export class ILSAdminCreateOrder extends XDialog<Maybe<Order>> {
  static override get element_name() {
    return 'ils-admin-create-order';
  }
  @state() user = new RXController<ILSUser | undefined>(this, undefined);
  @state() orderSelector = { _id: 1 } as const;
  @state() private readonly _futureAndJoinedDays = new RXController(
    this,
    this.user.observable.pipe(
      filter(Boolean),
      switchMap(user =>
        from(
          ExamDay.getFutureAndJoinedExamDays(user._id)({
            _id: 1,
            _class: 1,
            name: 1,
            candidateIDs: 1,
            maxCandidates: 1,
            location: { displayName: 1 },
          }),
        ).pipe(map(examDays => examDays.filter(exd => !exd.candidateIDs.includes(user._id)))),
      ),
    ),
  );

  private date = DateTime.now();
  private day: Maybe<ExamDay> = null;

  override render() {
    const user = this.user.value;
    if (!user) return html` no user provided `;
    return html`
      <x-flex space="md" padding="md" ${css({ maxWidth: this.sizes.maxReadableWidth })}>
        <x-h2> Neue Order anlegen </x-h2>
        <x-text>
          Du bist dabei einen neuen Kauf für Nutzer
          <b> ${user.ecommerce?.fullName ?? user.username} </b> anzulegen. <br />
          Dieser Kauf wird sofort als bezahlt eingetragen und kann danach nicht mehr über das UI gelöscht werden.
        </x-text>
        ${this.productSelector()}
        <x-input
          type="date"
          .value=${DateTime.now()}
          click-to-save
          placeholder="Rechnungsdatum"
          @date-picked=${(e: ValueEvent<DateTime>) => {
            this.date = e.detail.value;
          }}
        ></x-input>
        <x-flex horizontal space="md" space-between>
          <x-button mode="outline" @click=${() => this.close(null)}> Abbrechen </x-button>
          <x-button
            @click=${async () => {
              if (!this.day) return;
              try {
                const result = await XNativeDialog.waitFor(
                  createNewOrderManually(this.day._id, this.date, user._id)(this.orderSelector),
                  'Rechnung wird erstellt..',
                );

                void this.close(result);
              } catch (e: any) {
                if (e instanceof Error && e.message.includes('MailTemplate')) {
                  void XNativeDialog.showPopup<void>({
                    content: resolve => html`
                      <x-flex space="md" padding="md">
                        <x-h2>Mail-Fehler</x-h2>
                        <x-text>
                          Die für den Versand der Rechnung benötigten Mails scheinen einen Fehler zu beinhalten:
                        </x-text>
                        <x-text><b>${e.message}</b></x-text>
                        <x-text>Bitte Mail Templates bearbeiten, um eine Order anlegen zu können. </x-text>
                        <x-flex horizontal end space="md">
                          <x-button @click=${() => goTo({ path: '/start/mails', searchParams: NO_CHANGE })}>
                            Zu den Mail Templates
                          </x-button>
                          <x-button @click=${() => resolve()}> OK </x-button>
                        </x-flex>
                      </x-flex>
                    `,
                  });
                } else if (e instanceof Error && e.message) {
                  void XSnackbar.show(e.message);
                }

                logger.error(e);
                void this.close(null);
              }
            }}
          >
            Anlegen
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private productSelector() {
    return html`
      <x-dropdown-selection
        placeholder="Prüfungstag"
        .selectables=${this._futureAndJoinedDays.value}
        .renderString=${(day: ExamDay) =>
          html` <ils-examday-select-item .examDay=${day} single-line></ils-examday-select-item> `}
        .renderItem=${(day: ExamDay) => html` <ils-examday-select-item .examDay=${day}></ils-examday-select-item> `}
        @value-picked=${async (e: ValueEvent<ExamDay>) => {
          this.day = e.detail.value;
        }}
      ></x-dropdown-selection>
    `;
  }
}
